import { designService } from '@paragon/data-connections';
import {
  createProjectModel,
  projectCreated,
  saveAllProjectChanges,
  validateProjectHierarchyModelHasRequiredProperties,
} from '@paragon/layout-models';
import { getLoggedInUser } from '@paragon/login-tools';
import { getLoggedInUsersOrganizationMaterialSet } from '@paragon/material-lists';

import type { RootState } from '../../store';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from '../../store/actions/cameraActions';
import { ensureLevelIsVisible } from '../../store/actions/levelVisibilityActions';
import { getComponentDesignAnalysisStatus } from '../../store/selectors/componentDesignAnalysisStatusSelectors';
import { getCommittedComponentDesigns } from '../../store/selectors/getComponentDesignsSelectors';
import { getActiveLevelOrNull, getAllBuildings, getAllLevels } from '../../store/selectors/layoutDataSelectors';
import { isLayoutModeEnabled } from '../../store/selectors/layoutModeSelectors';
import { areWorkingChangesByComponentDesignGuidMap } from '../../store/selectors/workingChangesSelectors';
import { captureViewportPngOrNull } from '../../three/reactThreeFiberBindings';
import { getAssistantSnapshot } from '../snapshot';
import {
  describeJackConnections,
  describeJoints,
  findJointByName,
  openTruss,
  requireProject,
  requireString,
  summarizeTruss,
  waitForAppToSettle,
} from './helpers';
import {
  describeFloorArea,
  describeRoofPlane,
  describeTrussEnvelope,
  describeWall,
  describeWorkLine,
} from './layoutDescriptions';
import { describeCuts } from './memberCutTools';
import { defineTool, ToolError } from './types';

export { describeFloorArea, describeRoofPlane, shortId } from './layoutDescriptions';

export function getTrussAnalysisStatus(state: RootState, componentDesignGuid: string) {
  const hasWorkingChanges = areWorkingChangesByComponentDesignGuidMap(state)[componentDesignGuid] ?? false;
  const timestamps = state.componentDesignTimestamps[componentDesignGuid];
  const status = getComponentDesignAnalysisStatus(timestamps, hasWorkingChanges);
  return {
    status: status ?? 'analyzed',
    capacityRatio: status === 'unanalyzed' ? null : (timestamps?.lastAnalyzedCapacity ?? null),
  };
}

export function findLevelByName(state: RootState, name: string) {
  const level = getAllLevels(state).find(
    (candidate) => candidate.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (level == null)
    throw new ToolError(
      `There is no level named "${name}". The levels are ${getAllLevels(state)
        .map((candidate) => candidate.name)
        .join(', ')}.`,
    );
  return level;
}

function isOnLevel(levelGuid: string | null, objectLevelGuid: string | null) {
  return levelGuid == null || objectLevelGuid == null || objectLevelGuid === levelGuid;
}

export function describeLayout(state: RootState, levelGuid: string | null) {
  const { bearingEnvelopes, roofPlanes, trussEnvelopes } = state.layout;

  return {
    importedRoofs: Object.values(state.layout.roofContainers)
      .filter((container) => isOnLevel(levelGuid, container.levelGuid))
      .map((container) => container.name),
    walls: Object.values(bearingEnvelopes)
      .filter((envelope) => isOnLevel(levelGuid, envelope.levelGuid))
      .map(describeWall),
    roofPlanes: Object.values(roofPlanes)
      .filter((plane) => isOnLevel(levelGuid, plane.levelGuid))
      .map((plane) => describeRoofPlane(state, plane)),
    workLines: Object.values(state.layout.workLines).map(describeWorkLine),
    floorAreas: Object.values(state.layout.featurePatternContainers)
      .filter((container) => isOnLevel(levelGuid, container.levelGuid))
      .map((container) => describeFloorArea(state, container)),
    trussEnvelopes: Object.values(trussEnvelopes)
      .filter((envelope) => isOnLevel(levelGuid, envelope.levelGuid))
      .map((envelope) => describeTrussEnvelope(state, envelope)),
  };
}

export const contextTools = [
  defineTool<Record<string, never>>({
    name: 'get_project',
    description:
      'Reads the open project: its name, its buildings and levels, every truss with its analysis status, and what the designer is ' +
      'looking at.',
    inputSchema: { type: 'object', properties: {} },
    changesProject: false,
    async execute(_input, { getState }) {
      const state = getState();
      requireProject(state);
      return {
        summary: {
          // A new project has no levels or trusses yet; say outright that one is open so that isn't mistaken for none.
          projectOpen: true,
          levels: getAllLevels(state).map((level) => ({
            name: level.name,
            plateElevation: level.plateElevation,
            wallHeight: level.wallHeight,
            floorHeight: level.floorHeight,
          })),
          buildings: getAllBuildings(state).map((building) => building.name),
          trusses: getCommittedComponentDesigns(state).map((componentDesign) => ({
            name: componentDesign.component.name,
            type: componentDesign.component.componentType,
            ...getTrussAnalysisStatus(state, componentDesign.guid),
          })),
          ...getAssistantSnapshot(state),
        },
      };
    },
  }),

  defineTool<{ level?: string }>({
    name: 'get_layout',
    description:
      'Reads the layout of a level: its walls and beams, roof planes, and truss envelopes with the trusses designed ' +
      'in them. Points are in inches in the level’s plan coordinates. Defaults to the active level.',
    inputSchema: {
      type: 'object',
      properties: { level: { type: 'string', description: 'The level’s name.' } },
    },
    changesProject: false,
    async execute(input, { getState }) {
      const state = getState();
      requireProject(state);
      const level = input.level != null ? findLevelByName(state, input.level) : getActiveLevelOrNull(state);
      return { summary: { level: level?.name ?? null, ...describeLayout(state, level?.guid ?? null) } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_truss',
    description:
      'Opens a truss in the truss editor and reads it: members with their lumber and where each is (its centerline ' +
      'from end to end and its outline’s corners, in inches from the left end and up from the bottom, the ' +
      'coordinates add_members takes), bearings with their centers and widths, splices, gussets, jack-girder ' +
      'connections it’s part of, plies and spacing, what each member’s ends are cut against (the base end is ' +
      'its centerline’s `from`), joints ' +
      'from its analysis, and its latest analysis status.',
    inputSchema: {
      type: 'object',
      properties: { truss: { type: 'string', description: 'The truss’s name.' } },
      required: ['truss'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      return {
        summary: {
          ...summarizeTruss(componentDesign),
          jackConnections: describeJackConnections(getState(), componentDesign.guid),
          cuts: Object.fromEntries(
            componentDesign.component.members.map((member) => [
              member.name,
              describeCuts(member, componentDesign.component),
            ]),
          ),
          joints: componentDesign.analogModel?.analogJoints.map((joint) => ({
            name: `Joint ${joint.name}`,
            x: joint.x,
            y: joint.y,
          })),
          analysis: getTrussAnalysisStatus(getState(), componentDesign.guid),
        },
      };
    },
  }),

  defineTool<{ truss: string; joint: string }>({
    name: 'get_joint',
    description:
      'Reads where one joint is in a truss, by the name the analysis gives it (Joint 11). To find joints, or read ' +
      'more than one, use get_joints.',
    inputSchema: {
      type: 'object',
      properties: { truss: { type: 'string' }, joint: { type: 'string', description: 'For example "Joint 11".' } },
      required: ['truss', 'joint'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const joint = findJointByName(componentDesign, requireString(input, 'joint'));
      return { summary: { name: `Joint ${joint.name}`, x: joint.x, y: joint.y, boundary: joint.boundaryType } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_joints',
    description:
      'Lists every joint in a truss’s analysis by the name the analysis gives it (Joint 11), in one call: where it ' +
      'is, its support if it has one, the members that meet there, and the plates on it. Use it to find the joint ' +
      'an analysis result names, or the joint at a place, instead of reading joints one at a time.',
    inputSchema: {
      type: 'object',
      properties: { truss: { type: 'string', description: 'The truss’s name.' } },
      required: ['truss'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      return { summary: { truss: componentDesign.component.name, joints: describeJoints(componentDesign) } };
    },
  }),

  defineTool<{ view?: 'layout' | 'truss' }>({
    name: 'take_screenshot',
    description:
      'Takes a screenshot of what the designer sees in the layout or the truss editor. Use it only when you need to ' +
      'see your work, such as an overlapping web or a misplaced truss envelope.',
    inputSchema: {
      type: 'object',
      properties: { view: { type: 'string', enum: ['layout', 'truss'], description: 'Defaults to the current view.' } },
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      if (input.view != null && isLayoutModeEnabled(getState())) {
        dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate(input.view === 'layout' ? 'layout' : 'design'));
        await waitForAppToSettle(getState);
      }

      // Let the scene re-render after a view change before capturing it.
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      const image = captureViewportPngOrNull();
      if (image == null) throw new ToolError('The view isn’t ready to capture yet.');
      return { summary: { captured: getAssistantSnapshot(getState()) }, image };
    },
  }),
];

export const navigationTools = [
  defineTool<{ project: string }>({
    name: 'open_project',
    description:
      'Opens another project by name. The page reloads, which ends this conversation, so say so before calling it.',
    inputSchema: {
      type: 'object',
      properties: { project: { type: 'string', description: 'The project’s name.' } },
      required: ['project'],
    },
    changesProject: false,
    async execute(input) {
      const name = requireString(input, 'project').trim().toLowerCase();
      const summaries = await designService.projects.fetchSummaries();
      const matches = summaries.filter((summary) => summary.name.trim().toLowerCase() === name);
      if (matches.length === 0) throw new ToolError(`There is no project named "${input.project}".`);
      if (matches.length > 1) throw new ToolError(`More than one project is named "${input.project}".`);

      // Give the reply a moment to reach the designer before the page reloads.
      setTimeout(() => (window.location.href = `${window.location.origin}/${matches[0].guid}`), 1500);
      return { summary: { opening: matches[0].name } };
    },
  }),

  defineTool<{ name: string }>({
    name: 'create_project',
    description:
      'Creates a new, empty project and opens it, as Choose/Create Project > Create Project does, with the ' +
      'organization’s default materials. The page reloads, which ends this conversation, so say so before calling ' +
      'it. A new project has no buildings or levels; trusses can be made in it with create_truss straight away.',
    inputSchema: {
      type: 'object',
      properties: { name: { type: 'string', description: 'The new project’s name.' } },
      required: ['name'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const name = requireString(input, 'name').trim();
      if (name === '') throw new ToolError('A project’s name can’t be empty.');
      const user = getLoggedInUser(getState());
      const materialSet = getLoggedInUsersOrganizationMaterialSet(getState());
      if (user == null || materialSet == null)
        throw new ToolError('The organization’s materials haven’t loaded yet. Try again in a moment.');
      const summaries = await designService.projects.fetchSummaries();
      if (summaries.some((summary) => summary.name.trim().toLowerCase() === name.toLowerCase()))
        throw new ToolError(`A project named "${name}" already exists. Open it with open_project, or pick a new name.`);

      const project = createProjectModel(name, materialSet.defaultMaterials, user);
      const errors = validateProjectHierarchyModelHasRequiredProperties(project);
      if (errors) throw new ToolError(errors);
      dispatch(projectCreated({ model: project }));
      await dispatch(saveAllProjectChanges());

      // Give the reply a moment to reach the designer before the page reloads.
      setTimeout(() => (window.location.href = `${window.location.origin}/${project.guid}`), 1500);
      return { summary: { created: name, opening: name } };
    },
  }),

  defineTool<{ level: string }>({
    name: 'switch_level',
    description: 'Makes a level the active one in the layout.',
    inputSchema: {
      type: 'object',
      properties: { level: { type: 'string', description: 'The level’s name.' } },
      required: ['level'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const level = findLevelByName(getState(), requireString(input, 'level'));
      dispatch(ensureLevelIsVisible(level.guid, { makeActive: true, exclusive: true }));
      await waitForAppToSettle(getState);
      return { summary: { activeLevel: level.name } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'open_truss',
    description: 'Opens a truss in the truss editor.',
    inputSchema: {
      type: 'object',
      properties: { truss: { type: 'string', description: 'The truss’s name.' } },
      required: ['truss'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      return { summary: summarizeTruss(componentDesign) };
    },
  }),

  defineTool<Record<string, never>>({
    name: 'show_layout',
    description: 'Switches to the layout view.',
    inputSchema: { type: 'object', properties: {} },
    changesProject: false,
    async execute(_input, { dispatch, getState }) {
      if (!isLayoutModeEnabled(getState())) throw new ToolError('This project has no layout.');
      dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout'));
      await waitForAppToSettle(getState);
      return { summary: getAssistantSnapshot(getState()) };
    },
  }),
];
